import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MessageCircle, FileText, CheckCircle, Download } from 'lucide-react';

const EquipmentCard = ({ equipment }) => {
  const {
    manufacturer,
    model,
    category,
    description,
    mainImage,
    specifications,
    pdfUrl,
    available
  } = equipment;

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      `Olá, WellTec! Gostaria de consultar a disponibilidade e receber um orçamento de locação do equipamento ${manufacturer} ${model}.`
    );
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  const handlePDFView = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handlePDFDownload = async () => {
    if (pdfUrl) {
      try {
        // Tentar download direto primeiro
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${manufacturer}_${model}_folheto.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Erro ao baixar PDF:', error);
        // Fallback: abrir em nova aba
        window.open(pdfUrl, '_blank');
      }
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300 border-2 border-gray-100 h-full flex flex-col">
      {/* Imagem */}
      <div className="relative h-64 bg-gray-100 overflow-hidden">
        <img
          src={mainImage}
          alt={`${manufacturer} ${model}`}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
        />
        {/* Badge de categoria */}
        <div className="absolute top-4 left-4">
          <Badge className="bg-[#253551] text-white text-xs font-semibold px-3 py-1">
            {category === 'lavadora' ? 'Lavadora' : 'Varredeira'}
          </Badge>
        </div>
        {/* Badge de disponibilidade */}
        {available && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-green-600 text-white text-xs font-semibold px-3 py-1 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Disponível
            </Badge>
          </div>
        )}
      </div>

      <CardContent className="p-6 flex flex-col flex-grow">
        {/* Cabeçalho */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 font-medium">{manufacturer}</p>
          <h3 className="text-xl font-bold text-[#253551] mb-2">{model}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{description}</p>
        </div>

        {/* Especificações principais */}
        {specifications && (
          <div className="mb-4 bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-[#253551] mb-3">
              Especificações Técnicas
            </h4>
            <div className="space-y-2 text-sm">
              {specifications.type && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tipo:</span>
                  <span className="font-medium text-gray-900">{specifications.type}</span>
                </div>
              )}
              {specifications.cleaningWidth && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Largura de limpeza:</span>
                  <span className="font-medium text-gray-900">{specifications.cleaningWidth}</span>
                </div>
              )}
              {specifications.sweepingWidth && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Largura de varrição:</span>
                  <span className="font-medium text-gray-900">{specifications.sweepingWidth}</span>
                </div>
              )}
              {specifications.solutionTank && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tanque de solução:</span>
                  <span className="font-medium text-gray-900">{specifications.solutionTank}</span>
                </div>
              )}
              {specifications.recoveryTank && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tanque de recuperação:</span>
                  <span className="font-medium text-gray-900">{specifications.recoveryTank}</span>
                </div>
              )}
              {specifications.productivity && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Produtividade:</span>
                  <span className="font-medium text-gray-900">{specifications.productivity}</span>
                </div>
              )}
              {specifications.power && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Alimentação:</span>
                  <span className="font-medium text-gray-900">{specifications.power}</span>
                </div>
              )}
            </div>

            {/* Features destacadas */}
            {specifications.features && specifications.features.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Destaques:</p>
                <ul className="space-y-1">
                  {specifications.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="text-xs text-gray-600 flex items-start gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Botões de ação */}
        <div className="mt-auto space-y-2">
          {/* Botão WhatsApp */}
          <Button
            onClick={handleWhatsAppClick}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Solicitar Orçamento
          </Button>

          {/* Botões PDF (só aparecem se houver PDF) */}
          {pdfUrl ? (
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handlePDFView}
                variant="outline"
                className="border-[#253551] text-[#253551] hover:bg-[#253551] hover:text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                Ver Folheto
              </Button>
              <Button
                onClick={handlePDFDownload}
                variant="outline"
                className="border-[#253551] text-[#253551] hover:bg-[#253551] hover:text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar PDF
              </Button>
            </div>
          ) : (
            <p className="text-xs text-center text-gray-500 py-2">
              Folheto técnico em breve
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EquipmentCard;
